import { Injectable } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { WindowRef } from '@spartacus/core';

/**
 * Service for handling YouTube video URLs and extracting video IDs.
 * Supports multiple YouTube URL formats and plain video IDs.
 */
@Injectable({
  providedIn: 'root',
})
export class YouTubeVideoService {
  // Cache validated thumbnail qualities per video ID
  private validatedThumbnails = new Map<string, 'maxresdefault' | '0'>();

  // Track in-flight validation requests to prevent duplicate preloads
  private validatingThumbnails = new Set<string>();

  // Cache preload Image instances for cleanup
  private preloadImages = new Map<string, HTMLImageElement>();

  // Cache regex patterns for performance (avoid recompilation)
  private readonly VIDEO_ID_PATTERN = /^[\w-]{11}$/;
  private readonly URL_PATTERN =
    /^(?:https?:\/\/)?(?:www\.)?(?:youtu\.be\/([\w-]{11})|youtube\.com(?:\/embed\/|\/v\/|\/watch\?v=|\/watch\?.+&v=)([\w-]{11}))(?:.+)?$/i;

  // Prevent unbounded memory growth
  private readonly MAX_CACHE_SIZE = 100;

  constructor(
    private sanitizer: DomSanitizer,
    private winRef: WindowRef
  ) {}

  /**
   * Extracts YouTube video ID from a URL or plain video ID string.
   *
   * Supported inputs:
   * - YouTube URLs: https://www.youtube.com/watch?v=VIDEO_ID, https://youtu.be/VIDEO_ID, etc.
   * - Plain video IDs: dQw4w9WgXcQ
   *
   * @param source - YouTube URL or VIDEO_ID string
   * @returns Extracted VIDEO_ID or empty string if invalid
   */
  extractVideoId(source: string | undefined | null): string {
    const link = source?.trim();
    if (!link) {
      return '';
    }

    // If the source is a plain video ID (11 characters, alphanumeric with hyphens/underscores)
    if (this.VIDEO_ID_PATTERN.test(link)) {
      return link;
    }

    // If the source is a YouTube URL, use a regex pattern to match YouTube URL formats:
    // - Optional URL scheme (http or https)
    // - Optional www subdomain
    // - Host alternatives:
    //   - youtu.be/ (followed directly by VIDEO_ID)
    //   - youtube.com (with path alternatives: /embed/, /v/, /watch?v=, /watch?...&v=)
    // - Captures 11-character VIDEO_ID
    // - Optional other ending URL parameters
    const match = link.match(this.URL_PATTERN);

    // Return the first captured group (youtu.be) or second captured group (youtube.com)
    return match?.[1] || match?.[2] || '';
  }

  /**
   * Generates a YouTube embed URL with specified parameters.
   *
   * @param source - YouTube URL or video ID string
   * @param options - Embed configuration options
   * @returns Complete YouTube embed URL or empty string if invalid
   */
  getEmbedUrl(
    source: string | undefined | null,
    options: {
      autoplay?: boolean;
      controls?: boolean;
      modestbranding?: boolean;
      rel?: boolean;
      playsinline?: boolean;
      showinfo?: boolean;
    } = {}
  ): string {
    const videoId = this.extractVideoId(source);
    if (!videoId) {
      return '';
    }

    const params = new URLSearchParams(
      Object.entries({
        autoplay: options.autoplay ?? true,
        controls: options.controls ?? true,
        modestbranding: options.modestbranding ?? true,
        rel: options.rel ?? false,
        playsinline: options.playsinline ?? false,
        showinfo: options.showinfo ?? false,
      }).map(([key, value]) => [key, value ? '1' : '0'])
    );

    return `https://www.youtube.com/embed/${videoId}?${params.toString()}`;
  }

  /**
   * Generates a safe YouTube embed URL for use in Angular templates with [src] binding.
   *
   * @param source - YouTube URL or video ID string
   * @param options - Embed configuration options
   * @returns SafeResourceUrl for Angular DomSanitizer
   */
  getSafeEmbedUrl(
    source: string | undefined | null,
    options?: Parameters<typeof this.getEmbedUrl>[1]
  ): SafeResourceUrl {
    const url = this.getEmbedUrl(source, options);
    return this.sanitizer.bypassSecurityTrustResourceUrl(url);
  }

  /**
   * Gets validated YouTube thumbnail URL with automatic fallback.
   * Preloads maxresdefault and falls back to 0.jpg if invalid (120x90 placeholder).
   *
   * Uses client-side Image preloading to avoid 404 errors in browser console.
   * Returns 0.jpg immediately, upgrades to maxresdefault after validation completes.
   *
   * @param source - YouTube URL or video ID string
   * @param onValidated - Optional callback to trigger change detection (required for OnPush components)
   * @returns YouTube thumbnail URL (0.jpg initially, upgrades to maxresdefault if available)
   */
  getThumbnailUrl(source: string | undefined | null, onValidated?: () => void): string {
    const videoId = this.extractVideoId(source);
    if (!videoId) {
      return '';
    }

    // Return cached result if already validated
    const validated = this.validatedThumbnails.get(videoId);
    if (validated) {
      return this.getUrl(source, validated);
    }

    // Skip validation on server or if already in progress
    if (!this.winRef.isBrowser() || this.validatingThumbnails.has(videoId)) {
      return this.getUrl(source, '0');
    }

    // Start async validation - mark as in-progress to prevent duplicate preloads
    this.validatingThumbnails.add(videoId);

    const testImg = new Image();
    this.preloadImages.set(videoId, testImg);

    const cleanup = (quality: 'maxresdefault' | '0') => {
      this.addToCache(videoId, quality);
      this.validatingThumbnails.delete(videoId);
      this.preloadImages.delete(videoId);
      onValidated?.();
    };

    testImg.onload = () => {
      // YouTube returns 120x90 placeholder when maxresdefault doesn't exist
      const isPlaceholder = testImg.naturalWidth === 120 && testImg.naturalHeight === 90;
      cleanup(isPlaceholder ? '0' : 'maxresdefault');
    };

    testImg.onerror = () => cleanup('0');
    testImg.src = this.getUrl(source, 'maxresdefault');

    // Return fallback while validating
    return this.getUrl(source, '0');
  }

  /**
   * Adds video ID to cache with LRU eviction when max size reached.
   *
   * @param videoId - YouTube video ID
   * @param quality - Validated thumbnail quality
   */
  private addToCache(videoId: string, quality: 'maxresdefault' | '0'): void {
    if (this.validatedThumbnails.size >= this.MAX_CACHE_SIZE) {
      // Remove oldest entry (first key in Map maintains insertion order)
      const firstKey = this.validatedThumbnails.keys().next().value;
      if (firstKey) {
        this.validatedThumbnails.delete(firstKey);
      }
    }
    this.validatedThumbnails.set(videoId, quality);
  }

  /**
   * Generates YouTube thumbnail URL for a specific quality.
   *
   * @param source - YouTube URL or video ID string
   * @param quality - Thumbnail quality: '0' (480x360), 'maxresdefault' (1920x1080)
   * @returns YouTube thumbnail URL or empty string if invalid
   */
  private getUrl(source: string | undefined | null, quality: string = 'maxresdefault'): string {
    const videoId = this.extractVideoId(source);
    if (!videoId) {
      return '';
    }

    return `https://img.youtube.com/vi/${videoId}/${quality}.jpg`;
  }

  /**
   * Checks if a string is a valid YouTube URL or video ID.
   *
   * @param source - YouTube URL or video ID string to validate
   * @returns True if valid YouTube URL or ID
   */
  isValidYouTubeUrl(source: string | undefined | null): boolean {
    return this.extractVideoId(source) !== '';
  }
}
